import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check, RefreshCw } from "lucide-react";
import config from "../../config";
import { ConversationWordmark } from "../components/OlliLogo";
import { FormError } from "../components/auth/fields";
import { HELP_URL, PRIVACY_URL, TERMS_URL } from "../components/auth/AuthLayout";
import { Checkbox } from "../components/crm/contactsKit";
import { Field, TextInput } from "../components/crm/leadsKit";
import { PageLoader } from "../components/PageLoader";
import { PrimaryButton } from "../components/ui";

/**
 * Public data deletion request page (no auth), mounted at /data-deletion. This
 * URL is the one configured in the Meta App Dashboard. Same calls as the old
 * pages/data-deletion/DataDeletion.js:
 *   (no ?code) form   -> POST /api/external/data-deletion-request { email, phone, notes }
 *                        -> { success, confirmation_code, status_url, message }
 *   ?code=XYZ status  -> GET  /api/external/data-deletion-status?code=
 *                        -> { confirmation_code, status, status_label, received_at, completed_at, records_deleted, sla }
 * It renders outside the app shell, so the root carries the `olli` scope class.
 */

const STATUS_TONE = {
  pending: "bg-warning-soft text-warning",
  in_progress: "bg-sky-soft text-sky-deep",
  completed: "bg-success-soft text-success",
  rejected: "bg-danger-soft text-danger",
};
const fmtWhen = (v) => (v ? new Date(v).toLocaleString("en-IN") : null);
const textareaCls = "min-h-[96px] w-full rounded-sm border border-line bg-paper px-4 py-3 text-body text-ink outline-none placeholder:text-muted/70 hover:border-muted/50 focus:border-ink focus:ring-2 focus:ring-ink/10";
const cardCls = "rounded-lg bg-paper p-6 shadow-soft sm:p-8";
const linkCls = "font-medium text-sky-deep hover:underline";

export default function DataDeletion() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  return (
    <div className="olli min-h-screen bg-mist px-4 py-10 font-sans text-ink sm:py-14">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-8">
        <header className="flex flex-col items-center gap-5 text-center">
          <a href="https://getolliai.com" aria-label="Olli AI">
            <ConversationWordmark tone="white" className="h-8 w-auto" />
          </a>
          <div className="flex flex-col gap-2">
            <h1 className="text-h1 font-semibold">Data deletion request</h1>
            <p className="mx-auto max-w-md text-body text-muted">
              Request deletion of your personal data from Olli AI. We comply with deletion requests within 30 days.
            </p>
          </div>
        </header>

        {code ? <StatusView code={code} /> : <SubmitForm />}

        <footer className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-caption text-muted">
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="hover:text-ink">Privacy policy</a>
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="hover:text-ink">Terms of service</a>
          <a href="https://getolliai.com" className="hover:text-ink">getolliai.com</a>
        </footer>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <p className="text-caption font-medium uppercase tracking-wide text-muted">{children}</p>;
}

function SubmitForm() {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email && !phone) { setError("Provide at least one of email or phone."); return; }
    if (!agree) { setError("Please confirm you understand the SLA before submitting."); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${config.API_BASE_URL}/api/external/data-deletion-request`, {
        email: email.trim() || null,
        phone: phone.trim() || null,
        notes: notes.trim() || null,
      });
      if (r.data.success) setResult(r.data);
      else setError(r.data.message || "Request failed");
    } catch (err) {
      setError(err.response?.data?.message || "Could not submit request. Please try again or email support@getolliai.com.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className={`${cardCls} flex animate-fade-in flex-col items-center gap-5 text-center`}>
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success-soft text-success">
          <Check size={24} strokeWidth={2} />
        </span>
        <div className="flex flex-col gap-2">
          <h2 className="text-h3 font-semibold">Request received</h2>
          <p className="text-small text-muted">{result.message}</p>
        </div>
        <div className="w-full rounded-md bg-mist p-4 text-left">
          <Label>Confirmation code</Label>
          <p className="mt-1 break-all font-mono text-small">{result.confirmation_code}</p>
        </div>
        <p className="text-caption text-muted">
          Save this code. You can check the status of your request anytime at:
          <br />
          <a href={result.status_url} className={`${linkCls} break-all`}>{result.status_url}</a>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className={`${cardCls} flex flex-col gap-6`}>
      {error && <FormError>{error}</FormError>}
      <Field id="email" label="Email" hint="Used to identify your account and lead records.">
        <TextInput id="email" type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <Field id="phone" label="Phone" optional hint="Helpful if you contacted us via WhatsApp or SMS.">
        <TextInput id="phone" type="tel" autoComplete="tel" placeholder="+91 98765 43210" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </Field>
      <Field id="notes" label="Notes" optional>
        <textarea
          id="notes"
          rows={3}
          className={textareaCls}
          placeholder="Anything else we should know, e.g. the organisation name you submitted under or the approximate date of contact"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>
      <Checkbox id="agree" checked={agree} onChange={setAgree}>
        I understand that Olli AI will process this request within 30 days and that some records may be retained for legal or regulatory reasons (e.g. financial transactions, audit logs) per our{" "}
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className={linkCls}>Privacy policy</a>.
      </Checkbox>
      <PrimaryButton type="submit" loading={submitting} loadingLabel="Submitting…" className="w-full">
        Submit deletion request
      </PrimaryButton>
      <p className="border-t border-line pt-4 text-center text-caption text-muted">
        Need help? Email <a href={HELP_URL} className={linkCls}>support@getolliai.com</a>
      </p>
    </form>
  );
}

function StatusView({ code }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [canRetry, setCanRetry] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get(`${config.API_BASE_URL}/api/external/data-deletion-status?code=${encodeURIComponent(code)}`)
      .then((r) => {
        if (cancelled) return;
        if (r.data.success) setData(r.data);
        else { setError(r.data.message || "Not found"); setCanRetry(false); }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.message || "Could not load status");
        // 404 "Confirmation code not found" will not change on retry; a server or network error might.
        setCanRetry(!err.response || err.response.status >= 500);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [code]);

  useEffect(() => load(), [load]);

  if (loading) return <div className={cardCls}><PageLoader label="Loading status…" className="min-h-[200px]" /></div>;
  if (error) {
    return (
      <div className={`${cardCls} flex flex-col gap-5`}>
        <FormError>{error}</FormError>
        <div className="flex flex-wrap items-center gap-4">
          {canRetry && (
            <button type="button" onClick={load} className="flex h-10 items-center gap-1.5 rounded-full bg-paper px-4 text-small font-semibold text-ink ring-1 ring-line hover:bg-mist">
              <RefreshCw size={15} strokeWidth={1.75} />
              Try again
            </button>
          )}
          <a href="/data-deletion" className="flex items-center gap-2 text-small font-semibold text-sky-deep hover:underline">
            <ArrowLeft size={16} strokeWidth={1.75} />
            Submit a new request
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={`${cardCls} flex animate-fade-in flex-col gap-6`}>
      <div>
        <Label>Confirmation code</Label>
        <p className="mt-1 break-all font-mono text-small">{data.confirmation_code}</p>
      </div>
      <div>
        <Label>Status</Label>
        <span className={`mt-2 inline-flex rounded-full px-3 py-1.5 text-small font-medium ${STATUS_TONE[data.status] || "bg-mist text-ink"}`}>
          {data.status_label || data.status}
        </span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label>Received</Label>
          <p className="mt-1 text-small">{fmtWhen(data.received_at) || "—"}</p>
        </div>
        <div>
          <Label>Completed</Label>
          <p className="mt-1 text-small">{fmtWhen(data.completed_at) || "Pending"}</p>
        </div>
      </div>
      {data.records_deleted != null && data.status === "completed" && (
        <div className="border-t border-line pt-5">
          <Label>Records deleted</Label>
          <p className="mt-1 text-small">{data.records_deleted}</p>
        </div>
      )}
      <p className="border-t border-line pt-5 text-caption text-muted">
        Service level agreement: requests are processed within {data.sla || "30 days"} of receipt.
      </p>
    </div>
  );
}
